import React, { useCallback, useEffect } from "react";
import { OctopusError } from "~/client/resources";
import type { GitRefType } from "~/client/resources/versionControlledResource";
import type { Errors } from "~/components/DataBaseComponent/Errors";
import { isErrors } from "~/components/DataBaseComponent/Errors";
import { formatUrl } from "~/components/Navigation/ExternalLink/ExternalLink";
import { usePage } from "~/components/Page/Page";
import type IPageWrapper from "~/utils/pageId";
import { useAnalyticLinkLocation } from "./AnalyticLink";
import { useAnalyticSession } from "./AnalyticSession";
import type { ActionedFields, ActionFields, LinkFields, OnboardingFields, StepFields, ViewFields, StatusFields, OtherActionFields as SimpleActionFields, LogoFields, StepEditorFields } from "./AnalyticSession";

export interface ViewEvent {
    resource: string;
}

export interface ViewProps extends ViewEvent {
    // Override the name by specifying manually
    name?: string;
}

export type ResourceEvent = ViewEvent;

export enum Action {
    Add = "Add",
    Cancel = "Cancel",
    Clear = "Clear",
    Edit = "Edit",
    Deploy = "Deploy",
    Dismiss = "Dismiss",
    Save = "Save",
    Commit = "Commit",
    Select = "Select",
    Test = "Test",
    ServerResponse = "Server Response",
}

export interface ActionData {
    target?: string;
    stepTemplate?: string;
    source?: string;
}

export interface ActionEvent extends ResourceEvent {
    action: Action;
    data?: ActionData;
    isCaCenabled?: string | undefined;
    isDefaultBranch?: boolean;
    commitMessage?: boolean | undefined;
    status?: ActionStatus;
    gitRefType?: GitRefType;
}

export interface ActionResultEvent extends ActionEvent {
    status: ActionStatus;
}

export enum ActionStatus {
    Success = "Success",
    SucessWithWarning = "Success with Warning",
    Failed = "Failed",
}

interface TrackedActionEvent extends ActionEvent {
    error: string | undefined;
    statusCode: number | undefined;
    status: ActionStatus;
    duration: number;
}

export interface LinkEvent {
    linkLabel: string;
    documentUrl: string | undefined;
}

export interface StepEvent extends ResourceEvent {
    action: Action;
    stepCategory: string;
    stepTemplate: string | undefined;
}

export interface StepEditorEvent extends ResourceEvent {
    action: Action;
    stepTemplate: string | undefined;
}

export enum OnboardingType {
    step = "Onboarding Step",
}

export interface OnboardingEvent {
    linkLabel: string;
    onboardingType: OnboardingType;
}

export enum LogoType {
    icon = "Icon",
    customImage = "Custom Image",
}

export interface LogoEvent extends ActionEvent, ResourceEvent {
    logoType: LogoType;
}

export type AnalyticsDispatchEvent<TEvent> = (name: string, event: TEvent) => void;

export type AnalyticErrorCallback = (error: string | OctopusError | Errors) => void;

export type AnalyticTrackedActionDispatcher = <T>(name: string, event: ActionEvent, inner: (cb: AnalyticErrorCallback) => Promise<T>) => Promise<T>;
export type AnalyticActionDispatcher = AnalyticsDispatchEvent<ActionEvent>;
export type AnalyticLinkDispatcher = AnalyticsDispatchEvent<LinkEvent>;
export type AnalyticStepDispatcher = AnalyticsDispatchEvent<StepEvent>;
export type AnalyticStepEditorDispatcher = AnalyticsDispatchEvent<StepEditorEvent>;
export type AnalyticOnboardingDispatcher = AnalyticsDispatchEvent<OnboardingEvent>;
export type AnalyticViewDispatcher = AnalyticsDispatchEvent<ViewEvent>;
export type AnalyticSimpleDispatcher = (name: string) => void;
export type AnalyticLogoDispatcher = AnalyticsDispatchEvent<LogoEvent>;

function getStatusText(code: number | undefined) {
    if (code === undefined) {
        return undefined;
    }

    switch (code) {
        case 400:
            return "Bad Request";
        case 401:
            return "Unauthorised";
        case 403:
            return "Forbidden";
        case 404:
            return "Not Found";
        case 409:
            return "Conflict";
        case 500:
            return "Server Error";
        case 503:
            return "Service Unavailable";
    }

    return "Error";
}

function toErrorCode(code: number | undefined) {
    return code === undefined ? undefined : `${code} - ${getStatusText(code)}`;
}

function DataFields(data?: ActionData) {
    return {
        ...Prop("Target Type", data?.target),
        ...Prop("Step Template", data?.stepTemplate),
        ...Prop("Source", data?.source),
    };
}

export function PageFields(page: IPageWrapper) {
    // Strip any " (Keywords)" from the Area string.
    return {
        "Page Area": page?.Area.replace(/ *\(.*\)/, ""),
        "Page Name": page?.Name,
    };
}

export function useAnalyticTrackedActionDispatch(projectId?: string | undefined): AnalyticTrackedActionDispatcher {
    const session = useAnalyticSession();
    const page = usePage();

    function sendTrackedActionEvent(name: string, event: TrackedActionEvent) {
        if (!page) {
            return;
        }

        const fields: ActionFields = {
            "Action Type": event.action,
            "Duration (ms)": event.duration.toFixed(2),
            ...PageFields(page),
            "Resource Type": event.resource,
            Status: event.status,
            ...DataFields(event.data),
            "Is CaC Enabled": event.isCaCenabled,
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
        };

        if (typeof event.commitMessage === "boolean") {
            fields["Commit Message"] = event.commitMessage ? "True" : "False";
        }

        if (typeof event.isDefaultBranch === "boolean") {
            fields["Is Default Branch"] = event.isDefaultBranch ? "True" : "False";
        }

        if (typeof event.gitRefType === "string") {
            fields["Git Ref Type"] = event.gitRefType;
        }

        const status: StatusFields = {
            "Action Type": event.action,
            ...Prop("Error Code", toErrorCode(event.statusCode)),
            ...Prop("Error Messages", event.error),
            Status: event.status,
            "Resource Type": event.resource,
        };

        session.track(name, fields);
        session.track(event.status, status);
    }

    async function dispatch<T>(name: string, event: ActionEvent, inner: (cb: AnalyticErrorCallback) => Promise<T>): Promise<T> {
        const start = performance.now();
        let errorList: Array<string> = [];
        let statusCode: number | undefined = undefined;
        let status: ActionStatus = ActionStatus.Success;

        const addErrors = (errors?: Array<string>, error?: string) => {
            if (errors && errors.length > 0) {
                errorList = [...errorList, ...errors];
            } else if (error) {
                errorList = [...errorList, error];
            }
        };

        const onError = (error: string | OctopusError | Errors) => {
            status = ActionStatus.Failed;
            if (error instanceof OctopusError) {
                statusCode = statusCode ?? error.StatusCode;
                addErrors(error.Errors, error.ErrorMessage);
            } else if (isErrors(error)) {
                statusCode = statusCode ?? error.statusCode;
                addErrors(error.errors, error.message);
            } else {
                addErrors(undefined, error);
            }
        };

        try {
            return await inner(onError);
        } catch (e) {
            if (e instanceof OctopusError) {
                onError(e);
            } else if (e instanceof Error) {
                onError(e.message);
            }

            throw e;
        } finally {
            const error = errorList && errorList.length > 0 ? JSON.stringify(errorList) : undefined;
            sendTrackedActionEvent(name, { ...event, error, status, statusCode, duration: performance.now() - start });
        }
    }

    return dispatch;
}

export function useAnalyticActionDispatch(projectId?: string | undefined): AnalyticActionDispatcher {
    const session = useAnalyticSession();
    const page = usePage();

    return (name: string, event: ActionEvent) => {
        if (!page) {
            return;
        }

        const fields: ActionedFields = {
            "Action Type": event.action,
            ...PageFields(page),
            "Resource Type": event.resource,
            ...DataFields(event.data),
            Status: event.status,
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
        };

        if (typeof event.commitMessage === "boolean") {
            fields["Commit Message"] = event.commitMessage ? "True" : "False";
        }

        if (typeof event.isDefaultBranch === "boolean") {
            fields["Is Default Branch"] = event.isDefaultBranch ? "True" : "False";
        }

        if (typeof event.gitRefType === "string") {
            fields["Git Ref Type"] = event.gitRefType;
        }

        session.track(name, fields);
    };
}

export function useAnalyticExternalLinkDispatch(): (linkLabel: string, href: string) => void {
    const dispatchLink = useAnalyticLinkDispatch();
    return useCallback(
        (linkLabel: string, href: string) => {
            const ev: LinkEvent = {
                linkLabel,
                documentUrl: formatUrl(href),
            };
            dispatchLink(nameForDocument(href), ev);
        },
        [dispatchLink]
    );
}

export function useAnalyticLinkDispatch(): AnalyticLinkDispatcher {
    const session = useAnalyticSession();
    const linkLocation = useAnalyticLinkLocation();
    const page = usePage();

    return (name: string, event: LinkEvent) => {
        if (!page) {
            return;
        }

        const fields: LinkFields = {
            ...Prop("Document URL", event.documentUrl),
            "Link Label": event.linkLabel,
            "Link Location": linkLocation ?? "",
            ...PageFields(page),
        };

        session.track(name, fields);
    };
}

export function nameForDocument(href: string) {
    return `Visit Documentation`;
}

export function nameForLink(linkLabel: string) {
    return `Navigate to Page`;
}

export function nameForView(resource: string) {
    return `View ${resource}`;
}

export function useAnalyticStepDispatch(projectId?: string | undefined): AnalyticStepDispatcher {
    const session = useAnalyticSession();
    const page = usePage();

    return (name: string, event: StepEvent) => {
        if (!page) {
            return;
        }

        const fields: StepFields = {
            "Action Type": event.action,
            ...PageFields(page),
            "Resource Type": event.resource,
            "Step Category": event.stepCategory,
            ...Prop("Step Template", event.stepTemplate),
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
        };

        session.track(name, fields);
    };
}

export function useAnalyticsStepEditorDispatch(stepId: string, projectId?: string | undefined): AnalyticStepEditorDispatcher {
    const session = useAnalyticSession();
    const page = usePage();

    return (name: string, event: StepEditorEvent) => {
        if (!page) {
            return;
        }

        const fields: StepEditorFields = {
            "Action Type": event.action,
            ...PageFields(page),
            "Resource Type": event.resource,
            ...Prop("Step Template", event.stepTemplate),
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
            "Step Id": session.anonymize(stepId),
        };

        session.track(name, fields);
    };
}

export function useAnalyticOnboardingDispatch(): AnalyticOnboardingDispatcher {
    const session = useAnalyticSession();
    const linkLocation = useAnalyticLinkLocation();
    const page = usePage();

    return (name: string, event: OnboardingEvent) => {
        if (!page) {
            return;
        }

        const fields: OnboardingFields = {
            "Link Label": event.linkLabel,
            "Link Location": linkLocation ?? "",
            "Onboarding Type": event.onboardingType,
            ...PageFields(page),
        };

        session.track(name, fields);
    };
}

export function useAnalyticLogoDispatch(): AnalyticLogoDispatcher {
    const session = useAnalyticSession();
    const page = usePage();
    return (name: string, event: LogoEvent) => {
        if (!page) {
            return;
        }

        const fields: LogoFields = {
            "Action Type": Action.Save,
            "Resource Type": event.resource,
            "Logo Type": event.logoType,
            ...PageFields(page),
        };

        session.track(name, fields);
    };
}

export function useAnalyticViewDispatch(projectId?: string | undefined): AnalyticViewDispatcher {
    const session = useAnalyticSession();

    return (name: string, event: ViewEvent) => {
        const fields: ViewFields = {
            "Resource Type": event.resource,
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
        };

        session.track(name, fields);
    };
}

export function useAnalyticSimpleActionDispatch(): AnalyticSimpleDispatcher {
    const session = useAnalyticSession();
    const page = usePage();

    return (name: string) => {
        if (!page) {
            return;
        }

        const fields: SimpleActionFields = {
            ...PageFields(page),
        };

        session.track(name, fields);
    };
}

// <AnalyticView> element allows for the sending of a once-off view event when the element is rendered on the page
export function AnalyticView(props: ViewProps) {
    const dispatch = useAnalyticViewDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => dispatch(props.name ?? nameForView(props.resource), { resource: props.resource }), []);

    return <></>;
}

function Prop(name: string, value?: string): object {
    return value !== undefined ? { [name]: value } : {};
}
